import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiFetch,
  downloadAuthenticatedFile,
} from "../api/client";

const REFRESH_INTERVAL = 15000;

const statusOptions = [
  "",
  "new",
  "triage",
  "investigating",
  "contained",
  "remediating",
  "resolved",
  "closed",
  "false_positive",
];

const priorityOptions = ["", "P1", "P2", "P3", "P4"];
const severityOptions = ["", "critical", "high", "medium", "low", "info"];

function formatDate(value) {
  if (!value) return "Never";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function timeAgo(value) {
  if (!value) return "Never";

  const date = new Date(value);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (Number.isNaN(seconds)) return String(value);
  if (seconds < 60) return `${Math.max(0, seconds)} sec ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function dueText(value, completedAt) {
  if (completedAt) {
    return {
      label: "Completed",
      className: "soc-sla-complete",
    };
  }

  if (!value) {
    return {
      label: "Not set",
      className: "soc-sla-neutral",
    };
  }

  const due = new Date(value).getTime();
  const remaining = due - Date.now();

  if (Number.isNaN(due)) {
    return {
      label: String(value),
      className: "soc-sla-neutral",
    };
  }

  const absoluteMinutes = Math.floor(Math.abs(remaining) / 60000);

  if (remaining < 0) {
    if (absoluteMinutes < 60) {
      return {
        label: `${absoluteMinutes}m overdue`,
        className: "soc-sla-breached",
      };
    }

    const hours = Math.floor(absoluteMinutes / 60);

    return {
      label: `${hours}h overdue`,
      className: "soc-sla-breached",
    };
  }

  if (absoluteMinutes < 60) {
    return {
      label: `${absoluteMinutes}m remaining`,
      className:
        absoluteMinutes <= 15 ? "soc-sla-warning" : "soc-sla-good",
    };
  }

  const hours = Math.floor(absoluteMinutes / 60);

  if (hours < 24) {
    return {
      label: `${hours}h remaining`,
      className: hours <= 2 ? "soc-sla-warning" : "soc-sla-good",
    };
  }

  const days = Math.floor(hours / 24);

  return {
    label: `${days}d remaining`,
    className: "soc-sla-good",
  };
}

function IncidentQueuePage() {
  const navigate = useNavigate();

  const [incidents, setIncidents] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [search, setSearch] = useState("");

  const loadIncidents = useCallback(async (manual = false) => {
    try {
      manual ? setRefreshing(true) : setLoading(true);
      setError("");

      const params = new URLSearchParams();

      if (statusFilter) params.set("status", statusFilter);
      if (priorityFilter) params.set("priority", priorityFilter);
      if (severityFilter) params.set("severity", severityFilter);

      params.set("limit", "250");

      const [queueResponse, summaryResponse] = await Promise.all([
        apiFetch(`/incidents?${params.toString()}`),
        apiFetch(`/incidents/summary`),
      ]);

      if (!queueResponse.ok) {
        throw new Error(`Queue request failed: ${queueResponse.status}`);
      }

      if (!summaryResponse.ok) {
        throw new Error(`Summary request failed: ${summaryResponse.status}`);
      }

      const queueData = await queueResponse.json();
      const summaryData = await summaryResponse.json();

      setIncidents(Array.isArray(queueData) ? queueData : []);
      setSummary(summaryData || {});
    } catch (loadError) {
      console.error("Incident queue load error:", loadError);
      setError("Unable to load the SOC incident queue.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, priorityFilter, severityFilter]);

  useEffect(() => {
    loadIncidents();

    const timer = window.setInterval(() => {
      loadIncidents(false);
    }, REFRESH_INTERVAL);

    return () => window.clearInterval(timer);
  }, [loadIncidents]);

  const filteredIncidents = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return incidents;

    return incidents.filter((incident) => {
      const values = [
        incident.incident_number,
        incident.title,
        incident.description,
        incident.company_name,
        incident.device_hostname,
        incident.device_uuid,
        incident.category,
        incident.source,
        incident.status,
        incident.priority,
        incident.severity,
      ];

      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [incidents, search]);

  const openCount = Number(summary.open || 0);
  const p1Count = Number(summary.p1 || 0);
  const p2Count = Number(summary.p2 || 0);
  const newCount = Number(summary.new || 0);
  const triageCount = Number(summary.triage || 0);
  const investigatingCount = Number(summary.investigating || 0);
  const slaBreached =
    Number(summary.acknowledgement_sla_breached || 0) +
    Number(summary.resolution_sla_breached || 0);

  if (loading) {
    return <div className="soc-loading">Loading SOC incident queue...</div>;
  }

  return (
    <div className="soc-queue-page">
      <section className="soc-queue-hero">
        <div>
          <div className="soc-eyebrow">24×7 Security Operations Center</div>
          <h2>Incident Queue</h2>
          <p>
            Prioritize, assign, investigate, contain, and resolve security
            incidents across all managed organizations.
          </p>
        </div>

        <div className="soc-queue-actions">
          <button
            type="button"
            className="soc-primary-button"
            onClick={() => navigate("/incidents/new")}
          >
            Create Incident
          </button>

          <button
            type="button"
            className="soc-secondary-button"
            disabled={refreshing}
            onClick={() => loadIncidents(true)}
          >
            {refreshing ? "Refreshing..." : "Refresh Queue"}
          </button>
        </div>
      </section>

      {error && <div className="soc-error">{error}</div>}

      <section className="soc-summary-grid">
        <button
          type="button"
          onClick={() => setStatusFilter("")}
          className="soc-summary-card"
        >
          <span>Open Incidents</span>
          <strong>{openCount}</strong>
          <small>All active cases</small>
        </button>

        <button
          type="button"
          onClick={() => setPriorityFilter("P1")}
          className="soc-summary-card soc-summary-critical"
        >
          <span>P1 Critical</span>
          <strong>{p1Count}</strong>
          <small>Immediate action</small>
        </button>

        <button
          type="button"
          onClick={() => setPriorityFilter("P2")}
          className="soc-summary-card soc-summary-high"
        >
          <span>P2 High</span>
          <strong>{p2Count}</strong>
          <small>High priority</small>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("new")}
          className="soc-summary-card"
        >
          <span>New</span>
          <strong>{newCount}</strong>
          <small>Awaiting triage</small>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("triage")}
          className="soc-summary-card"
        >
          <span>In Triage</span>
          <strong>{triageCount}</strong>
          <small>Being prioritized</small>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("investigating")}
          className="soc-summary-card"
        >
          <span>Investigating</span>
          <strong>{investigatingCount}</strong>
          <small>Active investigations</small>
        </button>

        <div className="soc-summary-card soc-summary-warning">
          <span>SLA Breaches</span>
          <strong>{slaBreached}</strong>
          <small>Requires escalation</small>
        </div>
      </section>

      <section className="soc-filter-bar">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search incident, endpoint, company, category..."
          className="soc-search-input"
        />

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          {statusOptions.map((option) => (
            <option key={option || "all-status"} value={option}>
              {option ? `Status: ${option}` : "All statuses"}
            </option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(event) => setPriorityFilter(event.target.value)}
        >
          {priorityOptions.map((option) => (
            <option key={option || "all-priority"} value={option}>
              {option ? `Priority: ${option}` : "All priorities"}
            </option>
          ))}
        </select>

        <select
          value={severityFilter}
          onChange={(event) => setSeverityFilter(event.target.value)}
        >
          {severityOptions.map((option) => (
            <option key={option || "all-severity"} value={option}>
              {option ? `Severity: ${option}` : "All severities"}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="soc-clear-button"
          onClick={() => {
            setSearch("");
            setStatusFilter("");
            setPriorityFilter("");
            setSeverityFilter("");
          }}
        >
          Clear
        </button>
      </section>

      <section className="soc-queue-panel">
        <div className="soc-panel-heading">
          <div>
            <span className="soc-eyebrow">Analyst work queue</span>
            <h3>Active Incidents</h3>
          </div>

          <span>{filteredIncidents.length} displayed</span>
        </div>

        <div className="soc-table-wrapper">
          <table className="soc-incident-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Incident</th>
                <th>Customer / Endpoint</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Assigned Team</th>
                <th>Age</th>
                <th>Acknowledge SLA</th>
              </tr>
            </thead>

            <tbody>
              {filteredIncidents.length > 0 ? (
                filteredIncidents.map((incident) => {
                  const ackSla = dueText(
                    incident.sla_ack_due_at,
                    incident.acknowledged_at
                  );

                  return (
                    <tr
                      key={incident.id}
                      onClick={() => navigate(`/incidents/${incident.id}`)}
                    >
                      <td>
                        <span
                          className={`soc-priority soc-priority-${String(
                            incident.priority || "P3"
                          ).toLowerCase()}`}
                        >
                          {incident.priority || "P3"}
                        </span>
                      </td>

                      <td>
                        <strong>
                          {incident.incident_number || `Incident ${incident.id}`}
                        </strong>
                        <span>{incident.title}</span>
                        <small>
                          {incident.category || incident.source || "uncategorized"}
                        </small>
                      </td>

                      <td>
                        <strong>{incident.company_name || "Unassigned"}</strong>
                        <span>
                          {incident.device_hostname ||
                            incident.device_uuid ||
                            "No endpoint"}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`soc-severity soc-severity-${String(
                            incident.severity || "medium"
                          ).toLowerCase()}`}
                        >
                          {incident.severity || "medium"}
                        </span>
                      </td>

                      <td>
                        <span className="soc-status">
                          {incident.status || "new"}
                        </span>
                      </td>

                      <td>
                        {incident.assigned_team || "Unassigned"}
                      </td>

                      <td title={formatDate(incident.created_at)}>
                        {timeAgo(incident.created_at)}
                      </td>

                      <td>
                        <span className={`soc-sla ${ackSla.className}`}>
                          {ackSla.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="soc-empty">
                    No incidents match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default IncidentQueuePage;
